<script setup>
import { onMounted, computed, ref, toRefs } from 'vue';
import { useStore } from 'vuex';
import { useTimeoutFn } from '@vueuse/core';
import { provideMessageContext } from './provider.js';
import { useTrack } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { LocalStorage } from 'shared/helpers/localStorage';
import { ACCOUNT_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  MESSAGE_TYPES,
  ATTACHMENT_TYPES,
  MESSAGE_VARIANTS,
  SENDER_TYPES,
  ORIENTATION,
  MESSAGE_STATUS,
  CONTENT_TYPES,
} from './constants';

import Avatar from 'next/avatar/Avatar.vue';

import TextBubble from './bubbles/Text/Index.vue';
import ActivityBubble from './bubbles/Activity.vue';
import ImageBubble from './bubbles/Image.vue';
import FileBubble from './bubbles/File.vue';
import AudioBubble from './bubbles/Audio.vue';
import VideoBubble from './bubbles/Video.vue';
import EmbedBubble from './bubbles/Embed.vue';
import InstagramStoryBubble from './bubbles/InstagramStory.vue';
import EmailBubble from './bubbles/Email/Index.vue';
import UnsupportedBubble from './bubbles/Unsupported.vue';
import ContactBubble from './bubbles/Contact.vue';
import DyteBubble from './bubbles/Dyte.vue';
import LocationBubble from './bubbles/Location.vue';
import CSATBubble from './bubbles/CSAT.vue';
import FormBubble from './bubbles/Form.vue';
import VoiceCallBubble from './bubbles/VoiceCall.vue';

import MessageError from './MessageError.vue';
import ContextMenu from 'dashboard/modules/conversations/components/MessageContextMenu.vue';
import { useBranding } from 'shared/composables/useBranding';

/**
 * @typedef {Object} Attachment
 * @property {number} id - Unique identifier for the attachment
 * @property {number} messageId - ID of the associated message
 * @property {'image'|'audio'|'video'|'file'|'location'|'fallback'|'share'|'story_mention'|'contact'|'ig_reel'} fileType - Type of the attachment (file or image)
 * @property {number} accountId - ID of the associated account
 * @property {string|null} extension - File extension
 * @property {string} dataUrl - URL to access the full attachment data
 * @property {string} thumbUrl - URL to access the thumbnail version
 * @property {number} fileSize - Size of the file in bytes
 * @property {number|null} width - Width of the image if applicable
 * @property {number|null} height - Height of the image if applicable
 */

/**
 * @typedef {Object} Sender
 * @property {Object} additional_attributes - Additional attributes of the sender
 * @property {Object} custom_attributes - Custom attributes of the sender
 * @property {string} email - Email of the sender
 * @property {number} id - ID of the sender
 * @property {string|null} identifier - Identifier of the sender
 * @property {string} name - Name of the sender
 * @property {string|null} phone_number - Phone number of the sender
 * @property {string} thumbnail - Thumbnail URL of the sender
 * @property {string} type - Type of sender
 */

/**
 * @typedef {Object} ContentAttributes
 * @property {string} externalError - an error message to be shown if the message failed to send
 */

/**
 * @typedef {Object} Props
 * @property {('sent'|'delivered'|'read'|'failed'|'progress')} status - The delivery status of the message
 * @property {ContentAttributes} [contentAttributes={}] - Additional attributes of the message content
 * @property {Attachment[]} [attachments=[]] - The attachments associated with the message
 * @property {Sender|null} [sender=null] - The sender information
 * @property {boolean} [private=false] - Whether the message is private
 * @property {number|null} [senderId=null] - The ID of the sender
 * @property {number} createdAt - Timestamp when the message was created
 * @property {number} currentUserId - The ID of the current user
 * @property {number} id - The unique identifier for the message
 * @property {number} messageType - The type of message (must be one of MESSAGE_TYPES)
 * @property {string|null} [error=null] - Error message if the message failed to send
 * @property {string|null} [senderType=null] - The type of the sender
 * @property {string} content - The message content
 * @property {boolean} [groupWithNext=false] - Whether the message should be grouped with the next message
 * @property {Object|null} [inReplyTo=null] - The message to which this message is a reply
 * @property {boolean} [isEmailInbox=false] - Whether the message is from an email inbox
 * @property {number} conversationId - The ID of the conversation to which the message belongs
 * @property {number} inboxId - The ID of the inbox to which the message belongs
 */

// eslint-disable-next-line vue/define-macros-order
const props = defineProps({
  id: { type: Number, required: true },
  messageType: {
    type: Number,
    required: true,
    validator: value => Object.values(MESSAGE_TYPES).includes(value),
  },
  status: {
    type: String,
    required: true,
    validator: value => Object.values(MESSAGE_STATUS).includes(value),
  },
  attachments: { type: Array, default: () => [] },
  content: { type: String, default: null },
  contentAttributes: { type: Object, default: () => ({}) },
  contentType: {
    type: String,
    default: 'text',
    validator: value => Object.values(CONTENT_TYPES).includes(value),
  },
  conversationId: { type: Number, required: true },
  createdAt: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  currentUserId: { type: Number, required: true }, // eslint-disable-line vue/no-unused-properties
  groupWithNext: { type: Boolean, default: false },
  // True when this message starts a sender-run (no same-sender message directly
  // before it). Used by TextBubble to render the sender label once per run.
  isFirstInGroup: { type: Boolean, default: true },
  inboxId: { type: Number, default: null }, // eslint-disable-line vue/no-unused-properties
  inboxSupportsReplyTo: { type: Object, default: () => ({}) },
  inReplyTo: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  isEmailInbox: { type: Boolean, default: false },
  // Set true by Creator-Chat's MessageList so the Telegram action set is
  // surfaced on EVERY bubble (incoming + outgoing) without depending on a
  // per-message inbox lookup.
  isTelegramChannel: { type: Boolean, default: false },
  private: { type: Boolean, default: false },
  additionalAttributes: { type: Object, default: () => ({}) }, // eslint-disable-line vue/no-unused-properties
  sender: { type: Object, default: null },
  senderId: { type: Number, default: null },
  senderType: { type: String, default: null },
  sourceId: { type: String, default: '' }, // eslint-disable-line vue/no-unused-properties
});

const emit = defineEmits(['retry']);

const contextMenuPosition = ref({});
const showBackgroundHighlight = ref(false);
const showContextMenu = ref(false);
const { t } = useI18n();
const route = useRoute();
// Used by removeBotReaction below to dispatch the conversations/updateMessage
// action after a Telegram un-react call so the pill disappears optimistically.
const store = useStore();
const inboxGetter = useMapGetter('inboxes/getInbox');
const inbox = computed(() => inboxGetter.value(props.inboxId) || {});
const { replaceInstallationName } = useBranding();

/**
 * Computes the message variant based on props
 * @type {import('vue').ComputedRef<'user'|'agent'|'activity'|'private'|'bot'|'template'>}
 */
const variant = computed(() => {
  if (props.private) return MESSAGE_VARIANTS.PRIVATE;

  if (props.isEmailInbox) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) {
      return MESSAGE_VARIANTS.EMAIL;
    }
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return MESSAGE_VARIANTS.EMAIL;
  }

  if (props.status === MESSAGE_STATUS.FAILED) return MESSAGE_VARIANTS.ERROR;
  if (props.contentAttributes?.isUnsupported)
    return MESSAGE_VARIANTS.UNSUPPORTED;

  if (props.contentAttributes?.externalEcho) {
    return MESSAGE_VARIANTS.AGENT;
  }

  const isBot =
    props.sender?.type === SENDER_TYPES.AGENT_BOT ||
    props.senderType === SENDER_TYPES.AGENT_BOT ||
    (!props.sender && !props.additionalAttributes?.senderName);
  if (isBot && props.messageType === MESSAGE_TYPES.OUTGOING) {
    return MESSAGE_VARIANTS.BOT;
  }

  const variants = {
    [MESSAGE_TYPES.INCOMING]: MESSAGE_VARIANTS.USER,
    [MESSAGE_TYPES.ACTIVITY]: MESSAGE_VARIANTS.ACTIVITY,
    [MESSAGE_TYPES.OUTGOING]: MESSAGE_VARIANTS.AGENT,
    [MESSAGE_TYPES.TEMPLATE]: MESSAGE_VARIANTS.TEMPLATE,
  };

  return variants[props.messageType] || MESSAGE_VARIANTS.USER;
});

const isBotOrAgentMessage = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) {
    return false;
  }
  // if an outgoing message is still processing, then it's definitely a
  // message sent by the current user
  if (
    props.status === MESSAGE_STATUS.PROGRESS &&
    props.messageType === MESSAGE_TYPES.OUTGOING
  ) {
    return true;
  }
  const senderId = props.senderId ?? props.sender?.id;
  const senderType = props.sender?.type ?? props.senderType;

  if (!senderType || !senderId) {
    return true;
  }

  if (
    [SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(
      senderType
    )
  ) {
    return true;
  }

  return senderType.toLowerCase() === SENDER_TYPES.USER.toLowerCase();
});

/**
 * Computes the message orientation based on sender type and message type
 * @returns {import('vue').ComputedRef<'left'|'right'|'center'>} The computed orientation
 */
const orientation = computed(() => {
  if (isBotOrAgentMessage.value) {
    return ORIENTATION.RIGHT;
  }

  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return ORIENTATION.CENTER;

  return ORIENTATION.LEFT;
});

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const gridClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'grid grid-cols-1fr',
    [ORIENTATION.RIGHT]: 'grid grid-cols-1fr',
  };

  return map[orientation.value];
});

const gridTemplate = computed(() => {
  // `reactions` row sits between the bubble and the error/status meta row.
  // Empty when there are no reactions — the row collapses to 0 height so
  // bubbles without reactions look exactly like before this feature.
  const map = {
    [ORIENTATION.LEFT]: `
      "bubble"
      "reactions"
      "meta"
    `,
    [ORIENTATION.RIGHT]: `
      "bubble"
      "reactions"
      "meta"
    `,
  };

  return map[orientation.value];
});

// Telegram-style reaction pills shown beneath the bubble.
//   - bot_reactions: emojis our agent set via setMessageReaction (persisted
//     in Telegram::SetReactionService → message.content_attributes).
//   - reactions: future home for incoming contact-reactions arriving via
//     Telegram's message_reaction webhook event (backend ingestion still
//     pending — until then this stays empty).
// Each emoji is its own pill. Tapping a bot-reaction pill removes that
// reaction; this mirrors how Telegram's own clients toggle reactions.
const telegramReactionPills = computed(() => {
  const ca = props.contentAttributes || {};
  // MessageList runs the message payload through useCamelCase before passing
  // it down, so bot_reactions becomes botReactions at this layer. Read both
  // shapes so we stay robust regardless of which path populated Vuex:
  //   - botReactions     → MessageList's useCamelCase output (steady state)
  //   - bot_reactions    → freshly dispatched updateMessage payload before
  //                        re-render (optimistic-update window)
  const bot = Array.isArray(ca.botReactions)
    ? ca.botReactions
    : Array.isArray(ca.bot_reactions)
      ? ca.bot_reactions
      : [];
  const inbound = Array.isArray(ca.reactions) ? ca.reactions : [];
  const botSet = new Set(bot.filter(Boolean));
  return [
    ...bot.filter(Boolean).map(emoji => ({ emoji, source: 'bot' })),
    // Skip an inbound (contact) emoji that's already shown as our own bot
    // reaction. On the MTProto path the raw-reaction relay can echo an emoji
    // both sides used; without this it would render twice.
    ...inbound
      .filter(emoji => emoji && !botSet.has(emoji))
      .map(emoji => ({ emoji, source: 'contact' })),
  ];
});
const hasReactions = computed(() => telegramReactionPills.value.length > 0);

const removeBotReaction = async () => {
  // Reuse the same react endpoint with empty emoji — server-side it sends
  // `reaction: []` to Telegram which clears the bot's reaction on that
  // message and persists the cleared state back into content_attributes.
  try {
    const TelegramActionsAPI = (
      await import('dashboard/api/telegramActions')
    ).default;
    const resp = await TelegramActionsAPI.reactToMessage(
      props.conversationId,
      props.id,
      ''
    );
    // Push the FULL refreshed message back into Vuex so the pill
    // disappears instantly — see MessageContextMenu#tgReact for the
    // same pattern on the set-reaction side. Partial payloads would
    // wipe out content/sender/status because ADD_MESSAGE replaces
    // the slot wholesale.
    if (resp?.data?.message) {
      store.dispatch('updateMessage', resp.data.message);
    }
  } catch (_) {
    /* fail-quiet: a failed un-react just leaves the pill visible until next refresh */
  }
};

const shouldGroupWithNext = computed(() => {
  if (props.status === MESSAGE_STATUS.FAILED) return false;

  return props.groupWithNext;
});

const shouldShowAvatar = computed(() => {
  if (props.messageType === MESSAGE_TYPES.ACTIVITY) return false;
  if (orientation.value === ORIENTATION.LEFT) return false;

  // Outgoing (RIGHT): no self-avatar — frees the 24px grid column so the bubble
  // hugs the right edge (incoming uses the #21 per-sender name label instead).
  return false;
});

const componentToRender = computed(() => {
  if (props.isEmailInbox && !props.private) {
    const emailInboxTypes = [MESSAGE_TYPES.INCOMING, MESSAGE_TYPES.OUTGOING];
    if (emailInboxTypes.includes(props.messageType)) return EmailBubble;
  }

  if (props.contentType === CONTENT_TYPES.INPUT_CSAT) {
    return CSATBubble;
  }

  if (
    [CONTENT_TYPES.INPUT_SELECT, CONTENT_TYPES.FORM].includes(props.contentType)
  ) {
    return FormBubble;
  }

  if (props.contentType === CONTENT_TYPES.VOICE_CALL) {
    return VoiceCallBubble;
  }

  if (props.contentType === CONTENT_TYPES.INCOMING_EMAIL) {
    return EmailBubble;
  }

  if (props.contentAttributes?.isUnsupported) {
    return UnsupportedBubble;
  }

  if (props.contentAttributes.type === 'dyte') {
    return DyteBubble;
  }

  const instagramSharedTypes = [
    ATTACHMENT_TYPES.STORY_MENTION,
    ATTACHMENT_TYPES.IG_STORY,
    ATTACHMENT_TYPES.IG_STORY_REPLY,
    ATTACHMENT_TYPES.IG_POST,
  ];
  if (instagramSharedTypes.includes(props.contentAttributes.imageType)) {
    return InstagramStoryBubble;
  }

  if (Array.isArray(props.attachments) && props.attachments.length === 1) {
    const fileType = props.attachments[0].fileType;

    if (!props.content) {
      if (fileType === ATTACHMENT_TYPES.IMAGE) return ImageBubble;
      if (fileType === ATTACHMENT_TYPES.FILE) return FileBubble;
      if (fileType === ATTACHMENT_TYPES.AUDIO) return AudioBubble;
      if (fileType === ATTACHMENT_TYPES.VIDEO) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.IG_REEL) return VideoBubble;
      if (fileType === ATTACHMENT_TYPES.EMBED) return EmbedBubble;
      if (fileType === ATTACHMENT_TYPES.LOCATION) return LocationBubble;
    }
    // Attachment content is the name of the contact
    if (fileType === ATTACHMENT_TYPES.CONTACT) return ContactBubble;
  }

  return TextBubble;
});

const shouldShowContextMenu = computed(() => {
  return !props.contentAttributes?.isUnsupported;
});

const isBubble = computed(() => {
  return props.messageType !== MESSAGE_TYPES.ACTIVITY;
});

const isMessageDeleted = computed(() => {
  return props.contentAttributes?.deleted;
});

const payloadForContextMenu = computed(() => {
  return {
    id: props.id,
    content_attributes: props.contentAttributes,
    content: props.content,
    conversation_id: props.conversationId,
    // Needed by the Telegram action handlers (pin-state, edit prefill).
    source_id: props.sourceId,
    message_type: props.messageType,
  };
});

// Is this message in a Telegram inbox? Creator-Chat is Telegram-only, so
// when true we surface the native Telegram action set (react / pin / edit /
// forward / delete-on-Telegram) instead of Chatwoot's generic options.
//
// Detection must be direction-agnostic: some incoming messages arrive
// without a reliable inboxId prop, so we fall back to the selected chat's
// inbox_id (stable across every bubble in the open conversation).
const getInbox = useMapGetter('inboxes/getInbox');
const selectedChatForTg = useMapGetter('getSelectedChat');
const isTelegramInbox = computed(() => {
  // Primary signal: explicit flag from Creator-Chat's MessageList — true
  // for every bubble, immune to per-message inboxId quirks.
  if (props.isTelegramChannel) return true;
  // Fallback for any non-Creator-Chat caller: resolve the inbox channel.
  const lookup = getInbox.value;
  if (typeof lookup !== 'function') return false;
  const inboxId = props.inboxId || selectedChatForTg.value?.inbox_id;
  const inbox = lookup(inboxId);
  return inbox?.channel_type === 'Channel::Telegram';
});

// Is this conversation in Telegram Business mode (Personal-Account inbox)?
// We detect this by the conversation's additional_attributes carrying a
// business_connection_id — written at message-ingest time when the webhook
// payload was a `business_message`. Empirically (#223, 2026-06-02): both
// Henning and Sven Personal-Account inboxes hold a business_connection_id;
// pure CChatCRM_Bot inboxes do not.
//
// Why we care: Telegram's Bot API setMessageReaction method does NOT accept
// business_connection_id. The July-2024 Bot API 7.7 update added the
// parameter to sendMessage/sendPhoto/sendVideo/etc. but explicitly NOT to
// setMessageReaction. Calling it for a business-mode message results in
// Telegram trying to react as the raw bot, which has no access to the
// private chat → "Bad Request: message to react not found". This is a
// hard API limitation we can't work around without MTProto (Phase 2).
const isBusinessConnectionConv = computed(() => {
  const conv = selectedChatForTg.value;
  return !!conv?.additional_attributes?.business_connection_id;
});

// MTProto (user-account) inbox? A user account can only delete-for-everyone its
// OWN messages, so we gate the Delete option to outgoing bubbles there (unlike
// the Bot path, which can delete either party's message in business mode).
const isMtprotoInbox = computed(() => {
  const lookup = getInbox.value;
  if (typeof lookup !== 'function') return false;
  const inboxId = props.inboxId || selectedChatForTg.value?.inbox_id;
  return lookup(inboxId)?.channel_type === 'Channel::TelegramUser';
});

const contextMenuEnabledOptions = computed(() => {
  const hasText = !!props.content;
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);

  const isOutgoing = props.messageType === MESSAGE_TYPES.OUTGOING;
  const isFailedOrProcessing =
    props.status === MESSAGE_STATUS.FAILED ||
    props.status === MESSAGE_STATUS.PROGRESS;

  // ---- Telegram inbox: native Telegram action set --------------------
  if (isTelegramInbox.value) {
    const hasSourceId = !!props.sourceId; // delivered to Telegram already
    const actionable =
      hasSourceId && !isFailedOrProcessing && !isMessageDeleted.value;
    return {
      // Native options worth keeping in a Telegram context:
      copy: hasText,
      replyTo:
        !props.private &&
        props.inboxSupportsReplyTo.outgoing &&
        !isFailedOrProcessing,
      // Telegram-native actions (handled inside MessageContextMenu via
      // TelegramActionsAPI). The `telegram` flag turns the section on.
      telegram: true,
      // React is gated on isBusinessConnectionConv because Telegram's Bot
      // API doesn't accept business_connection_id on setMessageReaction
      // (see comment on isBusinessConnectionConv). Hiding the emoji row
      // beats letting agents click into a guaranteed-fail toast.
      telegramReact: actionable && !isBusinessConnectionConv.value,
      telegramForward: actionable,
      telegramPin: actionable,
      telegramEdit: actionable && isOutgoing && hasText,
      // MTProto: only own (outgoing) messages can be deleted for everyone.
      telegramDelete: actionable && (!isMtprotoInbox.value || isOutgoing),
    };
  }

  // ---- Non-Telegram inboxes: unchanged generic Chatwoot options ------
  return {
    copy: hasText,
    delete:
      (hasText || hasAttachments) &&
      !isFailedOrProcessing &&
      !isMessageDeleted.value,
    cannedResponse: isOutgoing && hasText && !isMessageDeleted.value,
    copyLink: !isFailedOrProcessing,
    translate: !isFailedOrProcessing && !isMessageDeleted.value && hasText,
    replyTo:
      !props.private &&
      props.inboxSupportsReplyTo.outgoing &&
      !isFailedOrProcessing,
  };
});

const shouldRenderMessage = computed(() => {
  const hasAttachments = !!(props.attachments && props.attachments.length > 0);
  const isEmailContentType = props.contentType === CONTENT_TYPES.INCOMING_EMAIL;
  const isUnsupported = props.contentAttributes?.isUnsupported;
  const isAnIntegrationMessage =
    props.contentType === CONTENT_TYPES.INTEGRATIONS;
  const isFailedMessage = props.status === MESSAGE_STATUS.FAILED;
  const hasExternalError = !!props.contentAttributes?.externalError;

  return (
    hasAttachments ||
    props.content ||
    isEmailContentType ||
    isUnsupported ||
    isAnIntegrationMessage ||
    isFailedMessage ||
    hasExternalError
  );
});

function openContextMenu(e) {
  const target = e.target;
  const shouldSkipContextMenu =
    target?.classList?.contains('skip-context-menu') ||
    // Mobile tap-to-react fires on the whole [grid-area:bubble] CELL, which spans
    // the column — so a tap in the empty space BESIDE the bubble (the cell itself,
    // or the invisible swipe-reply hint) would wrongly open the menu. Only a tap on
    // the bubble content (a descendant) should. Skip taps that land on the cell.
    target?.classList?.contains('fg-bubble-cell') ||
    !!target?.closest?.('.fg-swipe-reply-hint') ||
    ['a', 'img'].includes(target?.tagName?.toLowerCase());
  if (shouldSkipContextMenu || getSelection().toString()) {
    return;
  }

  e.preventDefault();
  if (e.type === 'contextmenu') {
    useTrack(ACCOUNT_EVENTS.OPEN_MESSAGE_CONTEXT_MENU);
  }
  contextMenuPosition.value = {
    x: e.pageX || e.clientX,
    y: e.pageY || e.clientY,
  };
  showContextMenu.value = true;
}

function closeContextMenu() {
  showContextMenu.value = false;
  contextMenuPosition.value = { x: null, y: null };
}

// Mobile mode = a touch device OR the mobile-layout breakpoint (≤768px, the same
// threshold the Fangate shell uses for isMobile). In mobile mode we hide the ⋮
// trigger (see :hide-button below) and instead open the action + reaction menu on
// a TAP of the bubble (Telegram-style). openContextMenu already ignores taps on
// links/images and active text selections, so link taps + text selection still
// work. Desktop (fine pointer, wide) keeps the hover ⋮.
const isMobileLike = typeof window !== 'undefined'
  && !!window.matchMedia?.('(pointer: coarse), (max-width: 768px)')?.matches;
function onBubbleTap(e) {
  if (justSwiped) { justSwiped = false; return; }   // ignore the click a swipe leaves behind
  if (!isMobileLike) return;
  openContextMenu(e);
}

// --- Swipe-to-reply (mobile, Telegram/WhatsApp-style) -----------------------
// Drag a bubble toward the open space to reply to it — the same action the ⋮
// menu's "Reply" fires (handleReplyTo). Incoming bubbles (left-aligned) swipe
// RIGHT, outgoing (right-aligned) swipe LEFT — both move toward the centre, so
// the bubble never runs off the screen edge. The bubble tracks the finger and a
// reply arrow fades in; releasing past the threshold replies, otherwise it
// snaps back. Vertical / wrong-way drags are left alone so the list scrolls.
const SWIPE_TRIGGER = 56;   // px of drag needed to fire the reply
const SWIPE_MAX = 72;       // px the bubble is allowed to travel
const swipeProgress = ref(0);   // 0..SWIPE_MAX, distance dragged in the reply direction
const swiping = ref(false);
const swipeReplyEnabled = computed(
  () => isBubble.value && !!contextMenuEnabledOptions.value.replyTo
);
const swipeDir = computed(() => (orientation.value === ORIENTATION.RIGHT ? -1 : 1));
let swipeStartX = 0;
let swipeStartY = 0;
let swipeArmed = false;     // a touch began on a replyable bubble
let swipeActive = false;    // committed to a drag in the reply direction
let justSwiped = false;     // swallow the click the browser synthesizes after a swipe

function onSwipeStart(e) {
  justSwiped = false;
  swipeArmed = false;
  swipeActive = false;
  swiping.value = false;
  swipeProgress.value = 0;
  if (!isMobileLike || !swipeReplyEnabled.value) return;
  const t = e.touches?.[0];
  if (!t) return;
  swipeStartX = t.clientX;
  swipeStartY = t.clientY;
  swipeArmed = true;
}
function onSwipeMove(e) {
  if (!swipeArmed) return;
  const t = e.touches?.[0];
  if (!t) return;
  const dx = t.clientX - swipeStartX;
  const dy = t.clientY - swipeStartY;
  const along = dx * swipeDir.value;   // positive when dragging the reply way
  if (!swipeActive) {
    if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;          // too small to judge yet
    if (!(along > 0 && Math.abs(dx) > Math.abs(dy))) {          // vertical / wrong-way → let it scroll
      swipeArmed = false;
      return;
    }
    swipeActive = true;
    swiping.value = true;
  }
  swipeProgress.value = Math.min(Math.max(along, 0), SWIPE_MAX);
  if (e.cancelable) e.preventDefault();                        // suppress page scroll while dragging
}
function onSwipeEnd() {
  if (!swipeActive) { swipeArmed = false; return; }
  const triggered = swipeProgress.value >= SWIPE_TRIGGER;
  justSwiped = true;
  swiping.value = false;
  swipeProgress.value = 0;   // snap back (the CSS transition animates it)
  swipeActive = false;
  swipeArmed = false;
  if (triggered) handleReplyTo();
}

function handleReplyTo() {
  const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
  const { conversationId, id: replyTo } = props;

  LocalStorage.updateJsonStore(replyStorageKey, conversationId, replyTo);
  emitter.emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, props);
}

const avatarInfo = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    const { name, avatar_url, channel_type, medium } = inbox.value;
    const iconName = avatar_url
      ? null
      : getInboxIconByType(channel_type, medium);
    return {
      name: iconName ? '' : name || t('CONVERSATION.NATIVE_APP'),
      src: avatar_url || '',
      iconName,
    };
  }

  // If no sender, check for Slack (or other integration) sender info
  if (!props.sender) {
    const { senderName, senderAvatarUrl } = props.additionalAttributes || {};
    if (senderName) {
      return { name: senderName, src: senderAvatarUrl ?? '' };
    }
    return { name: t('CONVERSATION.BOT'), src: '' };
  }

  const { sender } = props;
  const { name, type, avatarUrl, thumbnail } = sender || {};

  // If sender type is agent bot, use avatarUrl
  if ([SENDER_TYPES.AGENT_BOT, SENDER_TYPES.CAPTAIN_ASSISTANT].includes(type)) {
    return {
      name: name ?? '',
      src: avatarUrl ?? '',
    };
  }

  // For all other senders, use thumbnail
  return {
    name: name ?? '',
    src: thumbnail ?? '',
  };
});

const avatarTooltip = computed(() => {
  if (props.contentAttributes?.externalEcho) {
    return replaceInstallationName(t('CONVERSATION.NATIVE_APP_ADVISORY'));
  }
  if (avatarInfo.value.name === '') return '';
  return `${t('CONVERSATION.SENT_BY')} ${avatarInfo.value.name}`;
});

const setupHighlightTimer = () => {
  if (Number(route.query.messageId) !== Number(props.id)) {
    return;
  }

  showBackgroundHighlight.value = true;
  const HIGHLIGHT_TIMER = 1000;
  useTimeoutFn(() => {
    showBackgroundHighlight.value = false;
  }, HIGHLIGHT_TIMER);
};

onMounted(setupHighlightTimer);

provideMessageContext({
  ...toRefs(props),
  isPrivate: computed(() => props.private),
  variant,
  orientation,
  isBotOrAgentMessage,
  shouldGroupWithNext,
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <div
    v-if="shouldRenderMessage"
    :id="`message${props.id}`"
    class="flex w-full mb-2 message-bubble-container"
    :data-message-id="props.id"
    :class="[
      flexOrientationClass,
      {
        'group-with-next': shouldGroupWithNext,
        'bg-n-alpha-1': showBackgroundHighlight,
      },
    ]"
  >
    <div v-if="variant === MESSAGE_VARIANTS.ACTIVITY">
      <ActivityBubble :content="content" />
    </div>
    <div
      v-else
      :class="[
        gridClass,
        {
          'gap-y-2': contentAttributes.externalError,
          'w-full': variant === MESSAGE_VARIANTS.EMAIL,
        },
      ]"
      class="gap-x-2"
      :style="{
        gridTemplateAreas: gridTemplate,
      }"
    >
      <div
        v-if="!shouldGroupWithNext && shouldShowAvatar"
        v-tooltip.left-end="avatarTooltip"
        class="[grid-area:avatar] flex items-end"
      >
        <Avatar v-bind="avatarInfo" :size="24" />
      </div>
      <div
        class="[grid-area:bubble] flex relative fg-bubble-cell"
        :class="{
          'ltr:ml-3 rtl:mr-3 justify-end': orientation === ORIENTATION.RIGHT,
          'ltr:mr-1 rtl:ml-1': orientation === ORIENTATION.LEFT,
          'min-w-0': variant === MESSAGE_VARIANTS.EMAIL,
          'fg-swiping': swiping,
        }"
        :style="swipeProgress ? { transform: `translateX(${swipeProgress * swipeDir}px)` } : undefined"
        @contextmenu="openContextMenu($event)"
        @click="onBubbleTap"
        @touchstart="onSwipeStart"
        @touchmove="onSwipeMove"
        @touchend="onSwipeEnd"
        @touchcancel="onSwipeEnd"
      >
        <span
          v-if="swipeReplyEnabled"
          class="fg-swipe-reply-hint text-n-slate-10"
          :class="orientation === ORIENTATION.RIGHT ? 'fg-hint-on-right' : 'fg-hint-on-left'"
          :style="{ opacity: Math.min(swipeProgress / SWIPE_TRIGGER, 1) }"
          aria-hidden="true"
        >
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="9 17 4 12 9 7" />
            <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
          </svg>
        </span>
        <Component :is="componentToRender" />
      </div>
      <div
        v-if="hasReactions"
        class="[grid-area:reactions] flex flex-wrap gap-1 mt-1"
        :class="flexOrientationClass"
      >
        <button
          v-for="(r, idx) in telegramReactionPills"
          :key="`${r.source}-${r.emoji}-${idx}`"
          type="button"
          class="msg-reaction-pill"
          :class="{ 'msg-reaction-pill--own': r.source === 'bot' }"
          :title="r.source === 'bot' ? 'Tap to remove your reaction' : 'Reaction from contact'"
          @mousedown.prevent
          @click.stop="r.source === 'bot' ? removeBotReaction() : null"
        >
          {{ r.emoji }}
        </button>
      </div>
      <MessageError
        v-if="contentAttributes.externalError"
        class="[grid-area:meta]"
        :class="flexOrientationClass"
        :error="contentAttributes.externalError"
        @retry="emit('retry')"
      />
    </div>
    <div v-if="shouldShowContextMenu" class="context-menu-wrap">
      <ContextMenu
        v-if="isBubble"
        :context-menu-position="contextMenuPosition"
        :is-open="showContextMenu"
        :enabled-options="contextMenuEnabledOptions"
        :message="payloadForContextMenu"
        :hide-button="isMobileLike"
        @open="openContextMenu"
        @close="closeContextMenu"
        @reply-to="handleReplyTo"
      />
    </div>
  </div>
</template>

<style lang="scss">
.group-with-next + .message-bubble-container {
  .left-bubble {
    @apply ltr:rounded-tl-sm rtl:rounded-tr-sm;
  }

  .right-bubble {
    @apply ltr:rounded-tr-sm rtl:rounded-tl-sm;
  }
}

// Swipe-to-reply (mobile): the bubble cell tracks the finger horizontally, then
// animates back on release. touch-action keeps vertical scrolling working while
// we own the horizontal drag.
.fg-bubble-cell {
  transition: transform 0.18s ease;
  touch-action: pan-y;
}
.fg-bubble-cell.fg-swiping {
  transition: none;            // follow the finger 1:1 mid-drag
}
.fg-swipe-reply-hint {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  opacity: 0;                  // the inline style ramps this up during the drag
}
.fg-swipe-reply-hint.fg-hint-on-left  { right: 100%; margin-right: 6px; }   // incoming: arrow trails on the left
.fg-swipe-reply-hint.fg-hint-on-right { left: 100%;  margin-left: 6px; }    // outgoing: arrow trails on the right
// Telegram-style reaction pill under each bubble. Borrow Telegram's look:
// slightly tinted background, soft border, sits just under the bubble edge.
// The "own" variant (bot_reactions = our agent's reaction) gets the teal
// accent so the agent immediately sees which reaction is theirs vs incoming.
.msg-reaction-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 1px 8px 2px;
  min-height: 22px;
  font-size: 13px;
  line-height: 1;
  border-radius: 999px;
  border: 1px solid rgba(31, 42, 42, 0.08);
  background: rgba(31, 42, 42, 0.05);
  color: inherit;
  cursor: default;
  transition: background 0.12s, transform 0.08s;
  user-select: none;
}
.msg-reaction-pill--own {
  background: rgba(13, 148, 136, 0.14);
  border-color: rgba(13, 148, 136, 0.30);
  cursor: pointer;
  &:hover { background: rgba(13, 148, 136, 0.22); transform: translateY(-0.5px); }
  &:active { transform: translateY(0); }
}
</style>
